import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { EditorStarterItem } from '../items/item-type';
import { changeItem } from '../state/actions/change-item';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { getCanCrop } from '../utils/get-crop-from-item';
import { isLeftClick } from '../utils/is-left-click';
import { scaleCropSourceAroundWindowCenter } from '../utils/scale-crop-source';
import { useCurrentStateAsRef, useWriteContext } from '../utils/use-context';

const HANDLE_SIZE = 12;

type CropSourceScaleCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

const cornerCursor: Record<
  CropSourceScaleCorner,
  React.CSSProperties['cursor']
> = {
  'top-left': 'nwse-resize',
  'bottom-right': 'nwse-resize',
  'top-right': 'nesw-resize',
  'bottom-left': 'nesw-resize',
};

// 부모(소스 박스 컨테이너) 기준 각 꼭짓점 위치. 소스 박스 = item.left/top/width/height.
const cornerPosition: Record<
  CropSourceScaleCorner,
  { left: string; top: string }
> = {
  'top-left': { left: '0%', top: '0%' },
  'top-right': { left: '100%', top: '0%' },
  'bottom-left': { left: '0%', top: '100%' },
  'bottom-right': { left: '100%', top: '100%' },
};

/**
 * 크롭 모드 전용 — 원본 영상의 크기를 조정하는 핸들. 크롭 프레임이 아니라 **원본 소스
 * 박스의 꼭짓점**에 붙는 마름모. 바깥으로 끌면 원본이 커지고(확대), 안으로 끌면 작아진다.
 * 크롭 프레임(캔버스 창)은 고정되고 그 안의 영상만 확대/축소된다(순수 변환이 창 보존).
 */
const CropSourceScaleHandleUnmemoized: React.FC<{
  itemId: string;
  corner: CropSourceScaleCorner;
}> = ({ itemId, corner }) => {
  const scale = useCanvasTransformationScale();
  const { setState } = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();

  // 드래그 중 컴포넌트가 언마운트되면(선택 해제·크롭 종료) 전역 리스너를 정리한다.
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  const size = Math.round(HANDLE_SIZE / scale);

  const style: React.CSSProperties = useMemo(() => {
    const pos = cornerPosition[corner];
    return {
      position: 'absolute',
      left: pos.left,
      top: pos.top,
      width: size,
      height: size,
      backgroundColor: 'white',
      border: `${Math.max(1, Math.round(2 / scale))}px solid var(--color-editor-starter-accent)`,
      transform: 'translate(-50%, -50%) rotate(45deg)',
      pointerEvents: 'auto',
      cursor: cornerCursor[corner],
    };
  }, [corner, size, scale]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      if (!isLeftClick(e)) return;

      const initialItem = stateAsRef.current.undoableState.items[itemId];
      if (!initialItem || !getCanCrop(initialItem)) return;

      // 소스 박스 중심에서 이 꼭짓점까지 거리 비율을 배율로 쓴다. 꼭짓점을 바깥으로
      // 끌면 거리가 커져 확대, 안으로 끌면 축소.
      const w = initialItem.width;
      const h = initialItem.height;
      const centerX = initialItem.left + w / 2;
      const centerY = initialItem.top + h / 2;
      const cornerX =
        corner === 'top-left' || corner === 'bottom-left'
          ? initialItem.left
          : initialItem.left + w;
      const cornerY =
        corner === 'top-left' || corner === 'top-right'
          ? initialItem.top
          : initialItem.top + h;
      const initialRadius = Math.hypot(cornerX - centerX, cornerY - centerY);
      if (initialRadius <= 0) return;

      const initialX = e.clientX;
      const initialY = e.clientY;
      let moved = false;

      const onPointerMove = (moveEvent: PointerEvent) => {
        moved = true;
        const offsetX = (moveEvent.clientX - initialX) / scale;
        const offsetY = (moveEvent.clientY - initialY) / scale;
        const pointerX = cornerX + offsetX;
        const pointerY = cornerY + offsetY;
        const currentRadius = Math.hypot(
          pointerX - centerX,
          pointerY - centerY,
        );
        const scaleFactor = currentRadius / initialRadius;
        const scaledItem = scaleCropSourceAroundWindowCenter(
          initialItem,
          scaleFactor,
        );

        setState({
          update: (state) =>
            changeItem(state, itemId, () => scaledItem as EditorStarterItem),
          commitToUndoStack: false,
        });
      };

      const cleanup = () => {
        globalThis.removeEventListener('pointermove', onPointerMove);
        globalThis.removeEventListener('pointerup', onPointerUp);
        dragCleanupRef.current = null;
      };

      function onPointerUp() {
        cleanup();
        // 실제 이동이 있었을 때만 undo 지점을 남긴다(클릭만 한 경우 no-op 커밋 방지).
        if (moved) {
          setState({ update: (state) => state, commitToUndoStack: true });
        }
      }

      dragCleanupRef.current = cleanup;
      globalThis.addEventListener('pointermove', onPointerMove, {
        passive: true,
      });
      globalThis.addEventListener('pointerup', onPointerUp);
    },
    [corner, itemId, scale, setState, stateAsRef],
  );

  return (
    <div
      style={style}
      onPointerDown={onPointerDown}
    />
  );
};

export const CropSourceScaleHandle = memo(CropSourceScaleHandleUnmemoized);
